<script setup>
import { computed, nextTick, onBeforeMount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import useServiceType from '../../../../Composables/Service/useServiceType.ts'
import useUser from '../../../../Composables/User/useUser.ts'
import useApi from '../../Composables/useApi'
import useConfig from '../../../../Composables/useConfig'
import { addValue, setValue } from '../../Utils/object'
import MessageList from '../Message/MessageList.vue'
import ServiceFormModal from '../Service/ServiceFormModal.vue'
import ServiceSelector from '../Service/ServiceSelector.vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import BlockContent from '../UI/Content/BlockContent.vue'
import FieldCreator from '../UI/Form/FieldCreator.vue'
import FormCreator from '../UI/Form/FormCreator.vue'
import ModalConfirmation from '../UI/Modal/ModalConfirmation.vue'
import ModalError from '../UI/Modal/ModalError.vue'
import ContractContributorInformation from './ContractContributorInformation.vue'
import ContractEquipmentInformation from './ContractEquipmentInformation.vue'
import ContractPrestationInformation from './ContractPrestationInformation.vue'
import ContractRentLevel from './ContractRentLevel.vue'
import DeliveryOrderAddressField from './DeliveryOrder/DeliveryOrderAddressField.vue'
import SignatureBlock from './SignatureBlock.vue'

const { t, te } = useI18n()
const { isTheme, messages } = useConfig()
const { user, hasRole } = useUser()
const { getJSON, deleteJSON, postJSON, patchJSON, downloadFile } = useApi()

const props = defineProps({
  contractId: { type: String, required: true },
  contract: { type: Object, required: true },
  def: { type: Object, required: true },
})

const emit = defineEmits(['update:contract'])

const directorId = ref(null)
const build = ref(false)
const esign = ref(null)
const formData = ref(null)
const loadingServices = ref(new Map())
const isSending = ref(false)
const selectedService = ref(null)
const selectedServiceType = ref(null)
const errors = ref([])
const formcontract = ref(null)
const itemsDirector = ref(null)
const modalError = ref(null)
const modalconfirmation = ref(null)
const supplier = ref({
  isUpdated: false,
  siren: null,
})

const isV2 = props.contract.useV2View ?? false
const endpoint = `${isV2 ? 'v2/' : ''}contracts`

const activeServiceTypes = computed(() => {
  return props.def.finance.serviceTypes.options.choices.filter(({ active }) => active)
})

const shouldDisplayBlocContent = computed(() => {
  return props.contract.others?.length > 0
})

const updateData = (newData) => {
  formData.value = newData
}

const globalBuild = ({ def, object, build: buildVal }) => {
  build.value = buildVal
  esign.value = object.esign
  directorId.value = object.director ? object.director.id : null

  if (
    object.client &&
    object.supplier &&
    object.client.id == object.supplier.id &&
    def.supplier &&
    def.supplier.options &&
    object.status == 'CREATED'
  ) {
    props.def.supplier.options.disabled = true
  }
  if (isTheme('lbp')) {
    if (def['salesman'] && def.salesman.options) {
      props.def.salesman.options.required = true
    }
  }
}

const handleIsSending = (bool) => {
  isSending.value = bool
}

const setDatas = (contract) => {
  const page = contract?.status !== 'CREATED' ? 'active-and-services' : 'contract'
  const isWallet = ['PAID', 'FINISH', 'ENDED', 'CLOSED', 'PROLONGATION', 'CESSION'].includes(contract.status)
  const context = isWallet ? '/portefeuille/' : '/contrats/'
  const filename = isWallet ? 'portefeuille.php' : 'contract.php'
  window.location = `${context}${filename}?i=${props.contractId}&p=${page}`
}

const getEsign = (val) => {
  esign.value = !val
}

const goToSimulation = () => {
  window.location = `../devis/simulation.php?i=${props.contract.simulationId}&p=devis&index=simulation`
}

const setEquipment = (json) => {
  addValue(props.contract.finance.equipment, json.equipment)
  for (const [index, service] of Object.entries(props.contract.finance.services)) {
    if (service.id == json.id) {
      setValue(props.contract.finance.services, index, json)
    }
  }
  emit('update:contract', props.contract)
}

const setRent = (rent) => {
  emit('update:contract', {
    ...props.contract,
    finance: {
      ...props.contract.finance,
      amount: rent.decisionAmount,
    },
    rentInfo: rent,
  })
}

const isSigned = () => {
  return !!props.contract.signedAt
}

const isDelivery = () => {
  return ['WAITING', 'ORDERED', 'CHECKED', 'DELIVERED', 'COMPLETE', 'PAID'].includes(props.contract.status)
}

const sendToggle = async (serviceTypes, serviceType, isActivating) => {
  if (!isActivating) {
    await disableService(serviceType)
  } else {
    await enableService(serviceType)
  }
}

const disableService = async (serviceType) => {
  loadingServices.value.set(serviceType.id, true)
  const requests = props.contract.finance.services
    .filter((service) => service.serviceType.id === serviceType.id)
    .map((service) => deleteJSON(`services/${service.id}`, { financeId: props.contract.finance.id, id: service.id }))

  try {
    await Promise.all(requests)
    setValue(
      props.contract.finance,
      'services',
      props.contract.finance.services.filter((service) => service.serviceType.id !== serviceType.id),
    )
  } catch (error) {
    alert(error)
  } finally {
    loadingServices.value.delete(serviceType.id)
  }
}

async function enableService(serviceType) {
  loadingServices.value.set(serviceType.id, true)
  const { shouldFillServiceSupplier } = useServiceType(serviceType)
  if (shouldFillServiceSupplier.value) {
    showPopupService(serviceType)
    loadingServices.value.delete(serviceType.id)
    return
  }

  const requests = []
  if (serviceType.equipmentExcluded === true) {
    requests.push(postJSON(`services`, { financeId: props.contract.finance.id, serviceType: serviceType.id }))
  } else {
    const equipments = [props.contract.equipment, ...props.contract.equipment.equipments]
    equipments
      .filter((equipment) => !equipment.isOutstanding)
      .forEach((equipment) => {
        requests.push(
          postJSON(
            `services`,
            { financeId: props.contract.finance.id, serviceType: serviceType.id, equipment: equipment.id },
            { displayError: false },
          ),
        )
      })
  }

  const responses = await Promise.allSettled(requests)
  const addedServices = responses.filter(({ status }) => status === 'fulfilled').map(({ value }) => value)

  addedServices.forEach((addedService) => {
    const indexToDelete = props.contract.finance.services.findIndex((service) => service.id === addedService.id)
    if (indexToDelete > -1) {
      props.contract.finance.services.splice(indexToDelete, 1)
    }
  })
  setValue(props.contract.finance, 'services', props.contract.finance.services.concat(addedServices))
  loadingServices.value.delete(serviceType.id)

  for (const currentServiceType of activeServiceTypes.value) {
    if (
      currentServiceType.serviceCategory.id === serviceType.serviceCategory.id &&
      currentServiceType.id !== serviceType.id &&
      (currentServiceType.exclusive === true || serviceType.exclusive === true)
    ) {
      setValue(currentServiceType, 'active', false)
      await disableService(currentServiceType)
    }
  }

  errors.value = responses.filter(({ status }) => status === 'rejected').map(({ reason }) => reason.response.detail)

  nextTick(() => {
    if (errors.value.length > 0) {
      modalError.value.open()
    } else if (isServiceTypeEditable(serviceType)) {
      showPopupService(serviceType)
    }
  })
}

const download = async (contractId, serviceId, documentTemplateId, contractNumber, documentTemplateCleanName) => {
  let downloadName
  if (contractNumber) {
    downloadName = `Contrat-${contractNumber}-${documentTemplateCleanName}.pdf`
  } else {
    downloadName = `Contrat-${contractId}-${documentTemplateCleanName}_contrat.pdf`
  }
  await downloadFile(`contracts/${contractId}/services/${serviceId}/templates/${documentTemplateId}`, downloadName)
}

const addBuyer = (data) => {
  formcontract.value.datas['signers.buyer'] = data.value
  if (props.contract.supplier.id === props.contract.client.id) {
    formcontract.value.datas['signers.salesman'] = data.value
    location.reload()
  }
}

const addSalesman = (data) => {
  formcontract.value.datas['salesman'] = data.value
}

const addSurrender = (data) => {
  formcontract.value.datas['signers.surrender'] = data.value
}

const updateSurrender = (user) => {
  props.contract.surrender = user
  emit('update:contract', props.contract)
}

const addSurrenderApproverSigners = (data) => {
  formcontract.value.datas['signers.surrenderApproverSigners'] = data.value
}

const updateSurrenderApproverSigners = (user) => {
  props.contract.surrenderApproverSigners = user
  emit('update:contract', props.contract)
}

const patchContract = async () => {
  if (!supplier.value.isUpdated) {
    patchContractNext()
    return
  }
  const result = await patchJSON(`contracts/${props.contractId}/supplier`, {
    sirenSupplier: supplier.value.siren,
  })
  formcontract.value.datas.sirenSupplier = supplier.value.siren
  props.contract.supplier = result.contract.supplier
  formcontract.value.datas.supplier = null
  props.def.salesman = result.config.salesman
  formcontract.value.def.salesman = result.config.salesman
  supplier.value.isUpdated = false
  nextTick(() => {
    patchContractNext()
  })
}

const getNested = (obj, path) => {
  return path.split('.').reduce((o, p) => (o ? o[p] : undefined), obj)
}

const patchContractNext = () => {
  if (!formcontract.value) {
    return
  }

  if (itemsDirector.value) {
    let form = itemsDirector.value.$refs.form
    formcontract.value.datas['dateBirthDirector'] = form.datas.dateBirthDirector
    formcontract.value.datas['directorJSON'] = JSON.stringify(form.datas.director)
  }
  if (!formcontract.value.datas['directorJSON'] && !formcontract.value.datas['director']) {
    formcontract.value.datas['directorJSON'] = null
    formcontract.value.errors['directorJSON'] = true
  }

  Object.entries(formcontract.value.errors)
    .filter(([, hasError]) => hasError)
    .forEach(([name]) => {
      const fieldDomName = name.replace('.', '-')
      const fieldInvalidFeedback = document.querySelector(`#invalid-feedback-${fieldDomName}`)
      const field = getNested(formcontract.value.def, name)
      const fieldValue = formcontract.value.datas[name]
      const isCheckbox = field.type === 'checkbox'
      const isMissing = isCheckbox ? fieldValue === null || fieldValue === undefined : !fieldValue

      if (field.options.required && isMissing) {
        fieldInvalidFeedback.innerHTML = te(`contract.fields.${name}.error`)
          ? t(`contract.fields.${name}.error`)
          : t('message.errors.missingField')
        formcontract.value.errors[name] = true
        return
      }

      fieldInvalidFeedback.innerHTML = ''
      formcontract.value.errors[name] = false
    })

  if (isV2 && esign.value && (formcontract.value.datas['signers.buyer'] || contractContainsActorType('MANAGER'))) {
    if (contractContainsActorType('MANAGER')) {
      formcontract.value.datas['signers.buyer'] = null
      formcontract.value.errors['signers.buyer'] = false
    }
  } else if (esign.value && (formcontract.value.datas['buyer'] || contractContainsActorType('MANAGER'))) {
    // TODO: remove once v2 will be for lbp too
    if (contractContainsActorType('MANAGER')) {
      formcontract.value.datas['buyer'] = null
      formcontract.value.errors['buyer'] = false
    }
  }

  if (Object.values(formcontract.value.errors).some((error) => error === true)) {
    const firstErrorField = Object.keys(formcontract.value.errors).find(
      (key) => formcontract.value.errors[key] === true,
    )
    const firstErrorElement = document.querySelector(`#invalid-feedback-${firstErrorField.replace('.', '-')}`)
    if (firstErrorElement) {
      firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
    }
    return
  }

  formcontract.value.methods()
}

const cancelContract = () => {
  patchJSON(`contracts/${props.contractId}/cancelContract`, {}).then(
    () => {
      goToSimulation()
    },
    (reason) => {
      alert(reason)
    },
  )
}

const changeSupplier = async (siren, choices, label, fieldName, entity) => {
  if (entity === undefined) {
    entity = { id: undefined, siren }
    if (props.def.sirenSupplier?.options?.ajax === true) {
      const result = await getJSON(`entities?q=${siren}`)
      entity.id = result[0]?.id
    }
    getJSON(`entities/refresh?platformParamId=${props.platformParamId}&sirenSupplier=${siren}`)
  }
  formcontract.value.datas.supplier = entity.id
  supplier.value.siren = entity.siren
  supplier.value.isUpdated = true
}

const isServiceTypeActive = ({ id }) => {
  return activeServiceTypes.value.some((serviceType) => serviceType.id === id)
}

const onServiceEdit = async (serviceType) => {
  if (!isServiceTypeActive(serviceType)) {
    const serviceTypes = activeServiceTypes.value.reduce(
      (serviceTypesAcc, { id }) => Array.of(...serviceTypesAcc, id),
      [serviceType.id],
    )
    setValue(serviceType, 'active', true)
    await sendToggle(serviceTypes, serviceType, true)
  }

  loadingServices.value.set(serviceType.id, true)
  showPopupService(serviceType)
  loadingServices.value.delete(serviceType.id)
}

const onServiceUpdated = (service) => {
  const serviceIndex = props.contract.finance.services.findIndex(({ id }) => id === service.id)
  setValue(props.contract.finance.services, serviceIndex, service)
}

const showPopupService = (serviceType) => {
  selectedService.value =
    props.contract.finance.services.find((service) => service.serviceType.id === serviceType.id) ?? null
  selectedServiceType.value = null
  if (!selectedService.value) {
    selectedServiceType.value = serviceType
  }
}

const hidePopupService = () => {
  selectedService.value = null
}

const isServiceTypeEditable = (serviceType) => {
  if (props.contract.equipment.equipments.length > 0) {
    return false
  }

  const services = props.contract.finance.services.filter((service) => service.serviceType.id === serviceType.id)
  if (services.length > 1) {
    return false
  }

  return serviceType.editable
}

const showContractCancelModal = () => {
  modalconfirmation.value.showModal()
}

const contractContainsActorType = (type) => {
  return props.contract.finance.actors.some((actor) => actor.type === type)
}

onBeforeMount(() => {
  props.contract.withOrder = !props.contract.withOrder
})
</script>
<template>
  <div>
    <ModalConfirmation
      ref="modalconfirmation"
      message="contract.information.actionConfirmed.cancelContract"
      @confirm="cancelContract"
    />
    <FormCreator
      :id="Number(contractId)"
      ref="formcontract"
      :endpoint="endpoint"
      method="patch"
      :config-def="def"
      :config-object="contract"
      dynamic
      src-error="formcontract"
      class-to-add="d-none"
      @update-data="updateData"
      @global-build="globalBuild"
      @form-return="setDatas"
      @is-sending="handleIsSending"
    />

    <div v-if="build">
      <div class="row">
        <div class="col-md-12">
          <BlockContent inner-bloc-class="border-contract-blue" e2e-block="contractInformations">
            <div class="row d-flex">
              <div class="col col-md-4 flex-fill">
                <h4>{{ $t('contract.information.menu_params', { data: $t('contract.information.contributor') }) }}</h4>
                <ContractContributorInformation
                  :contract="contract"
                  :def="def"
                  :form-data="formData"
                  :is-v2="isV2"
                  @change-supplier="changeSupplier"
                />
              </div>
              <div class="col col-md-4 flex-fill border-information">
                <template v-if="isV2">
                  <h4>{{ $t('contract.information.signers') }}</h4>
                  <SignatureBlock
                    :object="contract"
                    :form-data="formData"
                    :form-instance="formcontract"
                    @add-buyer="addBuyer"
                    @add-salesman="addSalesman"
                    @add-surrender="addSurrender"
                    @update:surrender="updateSurrender"
                    @add-surrender-approver-signers="addSurrenderApproverSigners"
                    @update:surrender-approver-signers="updateSurrenderApproverSigners"
                  />
                </template>
                <template v-else>
                  <h4>
                    {{ $t('contract.information.menu_params', { data: $t('contract.list.table.equipment') }) }}
                  </h4>
                  <ContractEquipmentInformation :contract="contract" :def="def" />
                </template>
              </div>
              <div v-if="contract.finance" class="col col-md-4 flex-fill">
                <template v-if="isV2">
                  <h4>{{ $t('contract.information.menu_params', { data: $t('contract.list.table.equipment') }) }}</h4>
                  <ContractEquipmentInformation :contract="contract" :def="def" />
                  <hr style="border-top: 1px solid rgba(25, 55, 105, 0.15)" />
                </template>
                <h4>{{ $t('contract.information.menu_params', { data: $t('contract.information.prestation') }) }}</h4>
                <ContractPrestationInformation :contract="contract" :def="def" :form-data="formData" />
              </div>
            </div>
          </BlockContent>
        </div>
      </div>

      <!-- utilisation annuelle -->
      <BlockContent
        v-if="shouldDisplayBlocContent"
        title-t="contract.information.more"
        title-level="5"
        color="info"
        inner-bloc-class="border-contract-blue"
        e2e-block="contractContent"
      >
        <div class="card-body">
          <div class="row">
            <div v-for="(field, index) in contract.others" :key="'other-' + index" class="col-md-6">
              <FieldCreator
                :form-data="formData"
                :name="'others.' + index"
                form-ref="formcontract"
                domain="contract"
                label-top
                dynamic-label
              />
            </div>
          </div>
        </div>
      </BlockContent>

      <BlockContent
        v-if="
          def?.deliveryAddress?.options?.display ||
          def?.deliveryPlace?.options?.display ||
          def?.socialLandlord?.options?.display
        "
        title-t="contract.title.delivery"
        title-level="5"
        color="info"
        inner-bloc-class="border-contract-blue"
        e2e-block="contractDelivery"
      >
        <div class="card-body">
          <div class="row">
            <div v-if="def?.deliveryAddress?.options?.display" class="col-md-6">
              <DeliveryOrderAddressField :form="formData" :entity-id="contract.client.id" label-left />
            </div>
            <div
              v-if="def?.deliveryAddress?.options?.display && def?.deliveryPlace?.options?.display"
              class="col-md-1 d-flex justify-content-center align-items-center"
            >
              <p class="font-weight-bold mx-3 my-3" style="font-size: 20px">ou</p>
            </div>
            <div v-if="def?.deliveryPlace?.options?.display" class="col-md-5">
              <FieldCreator
                :form-data="formData"
                name="deliveryPlace"
                form-ref="formcontract"
                domain="contract"
                label-left
              />
            </div>
          </div>
          <div v-if="def?.socialLandlord?.options?.display" class="row">
            <div class="col-md-6">
              <FieldCreator
                :form-data="formData"
                name="socialLandlord"
                form-ref="formcontract"
                domain="contract"
                label-left
              />
            </div>
          </div>
        </div>
      </BlockContent>

      <BlockContent
        v-if="Object.keys(contract.formFields).length > 0 && isTheme('lbp')"
        title-t="contract.list.table.service"
        title-level="5"
        color="info"
        inner-bloc-class="border-contract-blue"
        e2e-block="contractService"
      >
        <div class="card-body">
          <div class="row">
            <div v-for="(field, index) in contract.formFields" :key="`formFields-${index}`" class="col-md-6">
              <FieldCreator
                :form-data="formData"
                :name="'formFields.' + index"
                form-ref="formcontract"
                domain="contract"
                label-left
                dynamic-label
                dynamic-form
                placeholder-config
              />
            </div>
          </div>
        </div>
      </BlockContent>

      <!-- assurance -->
      <BlockContent
        v-if="(Object.keys(contract.finance.services).length > 0 && isTheme('lbp')) || isTheme('olinn', 'financia')"
        :title-t="isTheme('lbp') ? 'insurance.title' : 'service.title'"
        title-level="5"
        color="info"
        inner-bloc-class="border-contract-blue"
        e2e-block="contractInsuranceService"
      >
        <ServiceSelector
          :def="def"
          :finance="contract.finance"
          :equipment="contract.equipment"
          :services="contract.finance.services"
          :duration="contract.rentInfo.duration"
          :is-signed="isSigned()"
          :loading="loadingServices"
          :readonly="!hasRole('ROLE_EDIT_CONTRACT_SERVICES')"
          @toggle-sent="sendToggle"
          @services-sent="setEquipment"
          @edit="onServiceEdit"
        />

        <div v-if="isTheme('lbp')" class="card-body">
          <table class="table table-bordered table-striped" style="background-color: white">
            <thead>
              <tr>
                <th class="fs-16">{{ $t('insurance.table.insurance') }}</th>
                <th class="fs-16">{{ $t('insurance.table.files') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="service in contract.finance.services.filter((service) => service.active)"
                :key="'service-' + service.id"
              >
                <td class="fs-16" v-html="service.serviceType.name"></td>
                <td class="fs-16">
                  <ul>
                    <li
                      v-for="documentTemplate in service.serviceType.documentTemplates"
                      :key="'docTemplate-' + documentTemplate.id"
                    >
                      <button
                        type="button"
                        class="btn btn-link"
                        @click="
                          download(
                            contract.id,
                            service.id,
                            documentTemplate.id,
                            contract.number,
                            documentTemplate.cleanName,
                          )
                        "
                        v-html="documentTemplate.name"
                      ></button>
                    </li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </BlockContent>

      <BlockContent
        v-if="def?.comment?.options?.display"
        :title-t="isTheme('lbp') ? 'contract.information.comment' : 'contract.fields.comment.label'"
        title-level="5"
        color="info"
        inner-bloc-class="border-contract-blue"
        e2e-block="contractComment"
      >
        <div class="card-body">
          <div class="col-md-12">
            <div class="form-group">
              <FieldCreator :form-data="formData" name="comment" form-ref="formcontract" domain="contract" />
            </div>
          </div>
        </div>
      </BlockContent>

      <BlockContent
        v-if="def?.esign?.options?.display"
        title-t="contract.title.esign"
        title-level="5"
        color="info"
        inner-bloc-class="border-contract-blue"
        e2e-block="contractEsign"
      >
        <div class="card-body">
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <FieldCreator
                  :form-data="formData"
                  name="esign"
                  form-ref="formcontract"
                  domain="contract"
                  refresh="true"
                  :reload="true"
                  force-refresh
                  label-left-equal="true"
                  @get-input-change="getEsign"
                ></FieldCreator>
              </div>
            </div>
          </div>
        </div>
      </BlockContent>

      <BlockContent
        title-t="contract.title.level"
        title-level="5"
        color="info"
        inner-bloc-class="border-contract-blue"
        e2e-block="contractLevel"
      >
        <ContractRentLevel
          :finance="contract.finance"
          :company="contract.company"
          :rent-info="contract.rentInfo"
          :def="def"
          :status="contract.status"
          @set-rent="setRent"
        />
      </BlockContent>

      <BlockContent
        v-if="messages"
        title-t="contract.information.message.title"
        title-level="5"
        color="info"
        inner-bloc-class="border-contract-blue"
        e2e-block="contractMessage"
      >
        <MessageList context="AppBundle\Entity\Contract" :context-id="contract.id" context-validate api-call />
      </BlockContent>

      <BlockContent
        v-if="def?.withoutOrder?.options?.display"
        title-t="contract.title.withOrder"
        title-level="5"
        color="info"
        inner-bloc-class="border-contract-blue"
        e2e-block="contractWithOrder"
      >
        <div class="card-body">
          <div class="row">
            <div class="col-md-8">
              <div class="form-group">
                <FieldCreator
                  :form-data="formData"
                  name="withoutOrder"
                  form-ref="formcontract"
                  domain="contract"
                  label-left-equal
                />
              </div>
            </div>
          </div>
        </div>
      </BlockContent>

      <div v-if="contract.supplier == null" class="row justify-content-center my-4">
        <span
          class="d-flex align-items-center mt-10 text-danger fs-18 font-weight-bold"
          e2e-data="contractInformationNoSupplier"
        >
          {{ $t('contract.information.noSupplier') }}
        </span>
      </div>
      <div class="row justify-content-center">
        <ButtonAll
          v-if="contract.simulationId !== null"
          size="large"
          label="contract.information.button.goToSimulation"
          :href="'../devis/simulation.php?i=' + contract.simulationId + '&p=devis&index=simulation'"
          type="information"
          name="gotToSimulation"
        />
        <ButtonAll
          v-if="!user.isReadOnly"
          size="large"
          label="button.validate_info"
          type="validation"
          icon="arrow"
          :loading="isSending"
          name="contractUpdate"
          @click="patchContract"
        />
      </div>
      <div v-if="!user.isReadOnly && !isDelivery()" class="row justify-content-center">
        <ButtonAll
          size="large"
          icon="cross"
          label="contract.information.button.cancel"
          message="contract.information.actionConfirmed.cancelContract"
          type="danger"
          name="contractDelete"
          @click="showContractCancelModal"
        />
      </div>

      <ServiceFormModal
        v-if="selectedService || selectedServiceType"
        :title="selectedService?.serviceType.label ?? selectedServiceType?.label"
        :service-id="selectedService?.id ?? null"
        :service-type-id="selectedService?.serviceType.id ?? selectedServiceType?.id"
        :finance-id="contract.finance.id"
        :duration="contract.finance.duration"
        @update="onServiceUpdated"
        @close="hidePopupService"
      />

      <ModalError v-if="errors.length > 0" id="modal-errors" ref="modalError" :errors="errors" />
    </div>
    <div v-else>
      <div class="row">
        <div class="col-md-12">
          <BlockContent>
            <div class="row d-flex">
              <div class="col col-md-4 flex-fill">
                <h4>{{ $t('contract.information.menu_params', { data: $t('contract.information.contributor') }) }}</h4>
                <div class="row py-3 mb-3 mx-3 loading"></div>
                <div class="row py-3 mb-3 mx-3 loading"></div>
                <div class="row py-3 mb-3 mx-3 loading"></div>
                <div class="row py-3 mb-3 mx-3 loading"></div>
              </div>
              <div class="col col-md-4 flex-fill border-information">
                <h4>{{ $t('contract.information.menu_params', { data: $t('contract.list.table.equipment') }) }}</h4>
                <div class="row py-3 mb-3 mx-3 loading"></div>
                <div class="row py-3 mb-3 mx-3 loading"></div>
                <div class="row py-3 mb-3 mx-3 loading"></div>
                <div class="row py-3 mb-3 mx-3 loading"></div>
              </div>
              <div class="col col-md-4 flex-fill">
                <h4>{{ $t('contract.information.menu_params', { data: $t('contract.information.prestation') }) }}</h4>
                <div class="row py-3 mb-3 mx-3 loading"></div>
                <div class="row py-3 mb-3 mx-3 loading"></div>
                <div class="row py-3 mb-3 mx-3 loading"></div>
                <div class="row py-3 mb-3 mx-3 loading"></div>
              </div>
            </div>
          </BlockContent>
        </div>
      </div>
    </div>
  </div>
</template>
